import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import StatsCard from '../components/StatsCard';
import RecentSales from '../components/RecentSales';
import {
  companyName, dayKey, downloadCsv, errorMessage, formatDate, formatMoney, formatQty,
  roleLabel, startOfMonth, unitPrice,
} from '../lib/format';

function CompanyDetail() {
  const { id } = useParams();

  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [monthSales, setMonthSales] = useState({ total: 0, profit: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const companySnap = await getDoc(doc(db, 'companies', id));
        if (!companySnap.exists()) {
          setNotFound(true);
          return;
        }
        setCompany({ id: companySnap.id, ...companySnap.data() });

        const salesRef = collection(db, 'companies', id, 'sales');
        const [usersSnap, productsSnap, recentSnap, monthSnap] = await Promise.all([
          getDocs(collection(db, 'companies', id, 'users')),
          getDocs(collection(db, 'companies', id, 'products')),
          // Antes se descargaban todas las ventas para quedarse con 10.
          getDocs(query(salesRef, orderBy('timestamp', 'desc'), limit(10))),
          getDocs(query(salesRef, where('timestamp', '>=', startOfMonth()), orderBy('timestamp', 'desc'))),
        ]);

        setUsers(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.role === 'admin' ? -1 : 0) - (b.role === 'admin' ? -1 : 0) || (a.name || '').localeCompare(b.name || '')));
        setProducts(productsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setSales(recentSnap.docs.map((d) => ({ id: d.id, companyId: id, ...d.data() })));

        const month = { total: 0, profit: 0, count: monthSnap.size };
        monthSnap.docs.forEach((d) => {
          const s = d.data();
          month.total += s.totalAmount || 0;
          month.profit += (s.totalAmount || 0) - (s.totalCost || 0);
        });
        setMonthSales(month);
      } catch (err) {
        console.error('Error fetching company details:', err);
        setError(errorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return <div className="empty-state"><span className="spinner spinner-dark" /></div>;
  }

  if (notFound) {
    return (
      <div className="empty-state">
        <div className="empty-icon">🏬</div>
        <p>No existe ninguna tienda con el id <span className="mono-text">{id}</span>.</p>
        <Link to="/companies" className="btn btn-secondary section-gap">← Volver a tiendas</Link>
      </div>
    );
  }

  const currency = company?.currency || 'USD';
  const tracked = products.filter((p) => p.trackInventory !== false);
  const lowStock = tracked
    .filter((p) => (Number(p.stock) || 0) <= (p.minStock ?? 5))
    .sort((a, b) => (a.stock || 0) - (b.stock || 0));
  const inventoryValue = tracked.reduce((sum, p) => sum + unitPrice(p) * Math.max(Number(p.stock) || 0, 0), 0);
  const companyMap = { [id]: company };

  const exportInventory = () => {
    downloadCsv(
      `inventario-${company.companyCode || id}-${dayKey(Date.now())}.csv`,
      ['Producto', 'Código de barras', 'Stock', 'Mínimo', 'Unidad', 'Precio por unidad', 'Costo por unidad', 'Controla inventario'],
      [...products]
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map((p) => {
          const pack = Number(p.packageQuantity) > 0 ? Number(p.packageQuantity) : 1;
          return [
            p.name || '', p.barcode || '', p.stock ?? 0, p.minStock ?? 5, p.unit || '',
            unitPrice(p).toFixed(2), ((Number(p.cost) || 0) / pack).toFixed(2), p.trackInventory === false ? 'No' : 'Sí',
          ];
        }),
    );
  };

  return (
    <div className="animate-fade-in">
      <Link to="/companies" className="back-link">← Tiendas</Link>
      <div className="page-header">
        <div>
          <h1 className="page-title">{companyName(company)}</h1>
          <p className="page-subtitle">
            Código <span className="mono-text">{company.companyCode || '—'}</span> · id <span className="mono-text">{id}</span>
          </p>
        </div>
        {products.length > 0 && (
          <button className="btn btn-secondary" onClick={exportInventory}>Exportar inventario</button>
        )}
      </div>

      {error && <div className="notice notice-error">Parte de la información no se pudo cargar. {error}</div>}

      <div className="stats-grid">
        <StatsCard title="Ventas del mes" value={formatMoney(monthSales.total, currency)} icon="💰" subtitle={`${monthSales.count} ventas`} />
        <StatsCard title="Ganancia del mes" value={formatMoney(monthSales.profit, currency)} icon="📈"
          subtitle={monthSales.total > 0 ? `Margen ${Math.round((monthSales.profit / monthSales.total) * 100)} %` : null} />
        <StatsCard title="Productos" value={products.length} icon="📦" subtitle={`Inventario a precio de venta: ${formatMoney(inventoryValue, currency)}`} />
        <StatsCard title="Bajo mínimo" value={lowStock.length} icon="⚠️" subtitle={`${lowStock.filter((p) => (p.stock || 0) <= 0).length} agotados`} />
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">Datos de la tienda</h3></div>
        <dl className="detail-grid">
          <div><dt>RIF</dt><dd>{company.rif || '—'}</dd></div>
          <div><dt>Teléfono</dt><dd>{company.phone || '—'}</dd></div>
          <div><dt>Moneda</dt><dd>{currency}</dd></div>
          <div><dt>Impuesto</dt><dd>{company.taxRate ?? 0} %</dd></div>
          <div><dt>Margen por defecto</dt><dd>{company.globalMargin ?? 0} %</dd></div>
          <div><dt>Impresora</dt><dd>{company.printerType ? `${company.printerType} ${company.printerIp || company.printerMac || ''}` : '—'}</dd></div>
          <div><dt>Alta</dt><dd>{formatDate(company.createdAt)}</dd></div>
        </dl>
      </div>

      <div className="tables-grid">
        <div className="card">
          <div className="card-header"><h3 className="card-title">Usuarios ({users.length})</h3></div>
          <div className="table-container">
            {users.length === 0 ? (
              <div className="empty-state"><div className="empty-icon">👥</div><p>No hay usuarios</p></div>
            ) : (
              <table>
                <thead><tr><th>Nombre</th><th>Acceso</th><th>Rol</th><th>Estado</th></tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td className="font-semibold">{u.name || u.displayName || '—'}</td>
                      <td className="text-secondary">{u.username ? `@${u.username}` : u.email || '—'}</td>
                      <td><span className={`badge ${u.role === 'admin' ? 'badge-dark' : 'badge-neutral'}`}>{roleLabel(u.role)}</span></td>
                      <td><span className={`badge ${u.active === false ? 'badge-danger' : 'badge-success'}`}>{u.active === false ? 'Suspendido' : 'Activo'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3 className="card-title">Stock bajo mínimo ({lowStock.length})</h3></div>
          <div className="table-container table-scroll">
            {lowStock.length === 0 ? (
              <div className="empty-state"><div className="empty-icon">✅</div><p>Todo por encima del mínimo</p></div>
            ) : (
              <table>
                <thead><tr><th>Producto</th><th className="num">Stock / mín.</th></tr></thead>
                <tbody>
                  {lowStock.map((p) => (
                    <tr key={p.id}>
                      <td className="font-semibold">{p.name || 'Sin nombre'}</td>
                      <td className="num">
                        <span className={(p.stock || 0) <= 0 ? 'text-danger font-bold' : 'text-warning font-bold'}>{formatQty(p.stock)}</span>
                        <span className="text-muted"> / {formatQty(p.minStock ?? 5)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <RecentSales recentSales={sales} companyMap={companyMap} showStore={false} />
    </div>
  );
}

export default CompanyDetail;
